import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useAuth } from '../context/AuthContext';
import { uploadAvatar, deleteAvatar, updateOwnPassword } from '../lib/api';
import { User, Camera, Trash2, Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { PasswordInput } from '../components/PasswordInput';

// ─── Helpers ────────────────────────────────────────────────────────────────

function centerAspectCrop(mediaWidth: number, mediaHeight: number) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, 1, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  );
}

async function getCroppedBlob(
  image: HTMLImageElement,
  crop: Crop,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const size = 400; // output size in px
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const cropX = (crop.x / 100) * image.width * scaleX;
  const cropY = (crop.y / 100) * image.height * scaleY;
  const cropW = (crop.width / 100) * image.width * scaleX;
  const cropH = (crop.height / 100) * image.height * scaleY;

  ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, size, size);

  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.92);
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

type Status = { type: 'success' | 'error'; message: string } | null;

export function ProfilePage() {
  const { user, refreshPlayer } = useAuth();
  const navigate = useNavigate();
  const player = user?.player;

  // ── Avatar state ──────────────────────────────────────────────────────────
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [isCropping, setIsCropping] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarStatus, setAvatarStatus] = useState<Status>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Password state ────────────────────────────────────────────────────────
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<Status>(null);

  // ── Redirect if not player ────────────────────────────────────────────────
  if (!user || !player) {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p>Pro zobrazení profilu se musíš přihlásit.</p>
      </div>
    );
  }

  // ── Handlers ─────────────────────────────────────────────────────────────

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result as string);
      setIsCropping(true);
      setCrop(undefined);
    };
    reader.readAsDataURL(file);
    // reset input so same file can be reselected
    e.target.value = '';
  }

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height));
  }, []);

  async function handleCropConfirm() {
    if (!imgRef.current || !crop || !player) return;
    setAvatarLoading(true);
    setAvatarStatus(null);

    const blob = await getCroppedBlob(imgRef.current, crop);
    if (!blob) {
      setAvatarStatus({ type: 'error', message: 'Nepodařilo se ořezat fotografii.' });
      setAvatarLoading(false);
      return;
    }

    const file = new File([blob], `avatar_${player.id}.jpg`, { type: 'image/jpeg' });
    const url = await uploadAvatar(player.id, file);

    if (url) {
      await refreshPlayer();
      setAvatarStatus({ type: 'success', message: 'Fotografie uložena!' });
    } else {
      setAvatarStatus({ type: 'error', message: 'Nahrávání selhalo, zkus to znovu.' });
    }

    setIsCropping(false);
    setImgSrc('');
    setAvatarLoading(false);
  }

  async function handleDeleteAvatar() {
    if (!player?.avatar_url) return;
    if (!window.confirm('Opravdu smazat profilovou fotografii?')) return;
    setAvatarLoading(true);
    const ok = await deleteAvatar(player.id, player.avatar_url);
    if (ok) {
      await refreshPlayer();
      setAvatarStatus({ type: 'success', message: 'Fotografie smazána.' });
    } else {
      setAvatarStatus({ type: 'error', message: 'Nepodařilo se smazat fotografii.' });
    }
    setAvatarLoading(false);
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setPasswordStatus(null);
    if (newPassword.length < 6) {
      setPasswordStatus({ type: 'error', message: 'Heslo musí mít alespoň 6 znaků.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'Hesla se neshodují.' });
      return;
    }
    setPasswordLoading(true);
    const ok = await updateOwnPassword(newPassword);
    if (ok) {
      setPasswordStatus({ type: 'success', message: 'Heslo bylo změněno!' });
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordStatus({ type: 'error', message: 'Změna hesla selhala, zkus to znovu.' });
    }
    setPasswordLoading(false);
  }

  const avatarSrc = player.avatar_url ?? null;

  return (
    <div className="page-container" style={{ maxWidth: 540, margin: '0 auto' }}>
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="btn-ghost btn-sm"
        style={{ marginBottom: '1.5rem', paddingLeft: '0.5rem' }}
      >
        <ArrowLeft size={15} /> Zpět
      </button>

      <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
        Můj profil
      </h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem', fontSize: '0.9rem' }}>
        {player.name} · {user.email}
      </p>

      {/* ── Avatar section ─────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid var(--border)',
      }}>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: '1rem', fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
          Profilová fotografie
        </h2>

        {/* Avatar preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{
            width: 88, height: 88, borderRadius: '50%',
            overflow: 'hidden', flexShrink: 0,
            background: 'var(--bg-base)',
            border: '2px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {avatarSrc ? (
              <img src={avatarSrc} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <User size={36} color="var(--text-muted)" />
            )}
          </div>
          <div>
            <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{player.name}</p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {avatarSrc ? 'Fotografie nahrána' : 'Žádná fotografie'}
            </p>
          </div>
        </div>

        {/* Status message */}
        {avatarStatus && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)',
            marginBottom: '1rem',
            background: avatarStatus.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            color: avatarStatus.type === 'success' ? '#22c55e' : '#ef4444',
            fontSize: '0.875rem',
          }}>
            {avatarStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {avatarStatus.message}
          </div>
        )}

        {/* Crop UI */}
        {isCropping && imgSrc ? (
          <div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Přetáhni a uprav výřez:
            </p>
            <ReactCrop
              crop={crop}
              onChange={c => setCrop(c)}
              aspect={1}
              circularCrop
              style={{ maxWidth: '100%', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}
            >
              <img
                ref={imgRef}
                src={imgSrc}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{ maxWidth: '100%', maxHeight: 340 }}
              />
            </ReactCrop>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                className="btn-primary"
                onClick={handleCropConfirm}
                disabled={avatarLoading}
                style={{ flex: 1 }}
              >
                {avatarLoading ? 'Nahrávám…' : 'Uložit fotografii'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => { setIsCropping(false); setImgSrc(''); }}
                disabled={avatarLoading}
              >
                Zrušit
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarLoading}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Camera size={16} />
              {avatarSrc ? 'Změnit fotografii' : 'Nahrát fotografii'}
            </button>
            {avatarSrc && (
              <button
                className="btn-danger"
                onClick={handleDeleteAvatar}
                disabled={avatarLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Trash2 size={16} />
                Smazat
              </button>
            )}
          </div>
        )}
      </section>

      {/* ── Password section ───────────────────────────────────────────── */}
      <section style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: '2rem',
        border: '1px solid var(--border)',
      }}>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: '1rem', fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
          Změna hesla
        </h2>

        {passwordStatus && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)',
            marginBottom: '1rem',
            background: passwordStatus.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            color: passwordStatus.type === 'success' ? '#22c55e' : '#ef4444',
            fontSize: '0.875rem',
          }}>
            {passwordStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {passwordStatus.message}
          </div>
        )}

        <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="new-password">Nové heslo</label>
            <div style={{ position: 'relative' }}>
              <Lock size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', zIndex: 1 }} />
              <PasswordInput
                id="new-password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimálně 6 znaků"
                style={{ paddingLeft: '2.25rem' }}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Potvrdit heslo</label>
            <div style={{ position: 'relative' }}>
              <Lock size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', zIndex: 1 }} />
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Zopakuj nové heslo"
                style={{ paddingLeft: '2.25rem' }}
                autoComplete="new-password"
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={passwordLoading || !newPassword || !confirmPassword}
            style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {passwordLoading ? 'Ukládám…' : '✓ Změnit heslo'}
          </button>
        </form>
      </section>
    </div>
  );
}
